/**
 * MODAL: BULK ASSIGN USER
 * Modale pour assigner un utilisateur à plusieurs entités en masse
 * Pattern basé sur AssignUserModal avec support du bulk
 */

import { useState, useMemo } from 'react';
import { Search, User, Users } from 'lucide-react';
import { Modal } from '../ui/modal';
import { Badge, Button } from '../ui';
import { useUsers } from '@/contexts/UsersContext';

interface BulkAssignUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Nombre d'entités sélectionnées */
  count: number;
  /** Nom de l'entité au pluriel (ex: "contacts", "prospects") */
  entityNamePlural: string;
  /** Callback appelé lors de l'assignation */
  onAssign: (userId: string) => Promise<void>;
  /** IDs des utilisateurs récemment utilisés (pour tri prioritaire) */
  recentUserIds?: string[];
  /** Titre personnalisé de la modale */
  title?: string;
}

export function BulkAssignUserModal({
  isOpen,
  onClose,
  count,
  entityNamePlural,
  onAssign,
  recentUserIds = [],
  title,
}: BulkAssignUserModalProps) {
  const { users, loading } = useUsers();
  const [searchTerm, setSearchTerm] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Titre par défaut
  const modalTitle = title || `Assigner ${count} ${entityNamePlural}`;

  // Filtrer et trier les utilisateurs
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Filtrer par terme de recherche
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = users.filter(
        (u) => u.full_name?.toLowerCase().includes(term) || u.email.toLowerCase().includes(term)
      );
    }

    // Trier : utilisateurs récents en premier, puis par nom
    return filtered.sort((a, b) => {
      const aIsRecent = recentUserIds.includes(a.id);
      const bIsRecent = recentUserIds.includes(b.id);

      if (aIsRecent && !bIsRecent) return -1;
      if (!aIsRecent && bIsRecent) return 1;

      // Sinon, trier par nom
      const aName = a.full_name || a.email;
      const bName = b.full_name || b.email;
      return aName.localeCompare(bName);
    });
  }, [users, searchTerm, recentUserIds]);

  // Trouver l'utilisateur sélectionné
  const selectedUser = useMemo(() => {
    return users.find((u) => u.id === selectedUserId);
  }, [users, selectedUserId]);

  async function handleConfirmAssign() {
    if (!selectedUserId) return;

    setIsAssigning(true);
    try {
      await onAssign(selectedUserId);
      handleClose();
    } finally {
      setIsAssigning(false);
    }
  }

  function handleClose() {
    setSearchTerm('');
    setSelectedUserId(null);
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={modalTitle} size="md">
      {/* Info sur la sélection */}
      <div className="mb-4 p-3 bg-zinc-50 rounded-lg border border-zinc-200">
        <div className="flex items-center gap-2 text-sm text-zinc-600">
          <Users className="size-4" />
          <span>
            <strong>{count}</strong> {entityNamePlural} sélectionné{count > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Champ de recherche */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher un utilisateur..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
            autoFocus
            disabled={loading || isAssigning}
          />
        </div>
      </div>

      {/* Liste des utilisateurs */}
      {loading ? (
        <div className="text-center py-8 text-sm text-zinc-500">Chargement...</div>
      ) : filteredUsers.length > 0 ? (
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {filteredUsers.map((user) => {
            const isRecent = recentUserIds.includes(user.id);
            const isSelected = user.id === selectedUserId;

            return (
              <button
                key={user.id}
                onClick={() => setSelectedUserId(user.id)}
                disabled={isAssigning}
                className={`w-full text-left p-3 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isSelected
                    ? 'border-zinc-900 bg-zinc-50 ring-1 ring-zinc-900'
                    : 'border-zinc-200 hover:bg-zinc-50'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className={`size-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-zinc-900' : 'bg-zinc-100'
                      }`}
                    >
                      <User className={`size-4 ${isSelected ? 'text-white' : 'text-zinc-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-zinc-900 truncate">
                        {user.full_name || user.email}
                      </div>
                      {user.full_name && (
                        <div className="text-sm text-zinc-500 truncate">{user.email}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isRecent && (
                      <Badge variant="secondary" className="text-xs">
                        Récent
                      </Badge>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-sm text-zinc-500">
          {searchTerm.trim() ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur disponible'}
        </div>
      )}

      {/* Footer avec boutons */}
      <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-zinc-200">
        <Button variant="outline" onClick={handleClose} disabled={isAssigning}>
          Annuler
        </Button>
        <Button onClick={handleConfirmAssign} disabled={!selectedUserId || isAssigning}>
          {isAssigning ? (
            'Assignation...'
          ) : (
            <>Assigner à {selectedUser?.full_name || selectedUser?.email || 'cet utilisateur'}</>
          )}
        </Button>
      </div>
    </Modal>
  );
}
